import { useState, useEffect, useCallback, useRef } from "react";
import { PageHeader, EmptyState, Skeleton } from "@/components/Primitives";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusChip } from "@/components/StatusChip";
import DocumentShareDialog from "@/components/DocumentShareDialog";
import { toast } from "sonner";
import {
  FileText, Upload, Search, Download, Share2, Archive, RotateCcw, Trash2, Lock, Globe, Users,
  Folder, FolderPlus, FolderOpen, MoreHorizontal, Pencil, ChevronRight, FolderInput,
} from "lucide-react";

const AUDIENCES = ["Admin Only", "Sales Managers", "Sales Representatives", "All Staff", "Customer-Facing", "Specific Users"];
const MAIN = "__main__";

export default function DocumentLibrary() {
  const { can } = useAuth();
  const isAdmin = can("tenant_owner", "platform_owner");

  const [docs, setDocs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [audFilter, setAudFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [moveDoc, setMoveDoc] = useState(null);
  const [selected, setSelected] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [current, setCurrent] = useState(null); // current folder object or null = main library
  const [folderDialog, setFolderDialog] = useState(null); // { mode:'create'|'rename', folder }
  const [delFolder, setDelFolder] = useState(null);

  const loadFolders = useCallback(() => {
    api.get("/library/folders").then((r) => setFolders(r.data.items || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (catFilter) params.set("category", catFilter);
    if (audFilter) params.set("audience", audFilter);
    if (showArchived) params.set("status", "Archived");
    params.set("folder", current ? current.id : "__root");
    api.get(`/library?${params}`).then((r) => setDocs(r.data.items)).finally(() => setLoading(false));
  }, [q, catFilter, audFilter, showArchived, current]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { api.get("/library/categories").then((r) => setCats(r.data.items)); }, []);

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const download = async (d) => {
    try {
      const { data } = await api.get(`/library/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a"); a.href = url; a.download = d.original_filename || d.title; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const archive = async (d) => { try { await api.delete(`/library/${d.id}`); toast.success("Archived"); load(); loadFolders(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };
  const restore = async (d) => { try { await api.post(`/library/${d.id}/restore`); toast.success("Restored"); load(); loadFolders(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };
  const purge = async (d) => { if (!window.confirm(`Permanently delete "${d.title}"? This cannot be undone.`)) return; try { await api.delete(`/library/${d.id}/purge`); toast.success("Permanently deleted"); load(); loadFolders(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };

  const saveFolder = async (name) => {
    try {
      if (folderDialog.mode === "rename") await api.put(`/library/folders/${folderDialog.folder.id}`, { name });
      else await api.post("/library/folders", { name });
      toast.success(folderDialog.mode === "rename" ? "Folder renamed" : "Folder created");
      setFolderDialog(null); loadFolders();
      if (folderDialog.mode === "rename" && current?.id === folderDialog.folder.id) setCurrent({ ...current, name });
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const doDeleteFolder = async () => {
    try {
      const { data } = await api.delete(`/library/folders/${delFolder.id}`);
      toast.success(`Folder deleted. ${data.documents_moved} document${data.documents_moved === 1 ? "" : "s"} moved to the main library.`);
      if (current?.id === delFolder.id) setCurrent(null);
      setDelFolder(null); loadFolders(); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const shownFolders = current ? [] : folders.filter((f) => !q || f.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="page-document-library">
      <PageHeader title="Document Library" subtitle="Central ChitChat resource center — training, media kits, agreements, pricing and more.">
        {isAdmin && !current && <Button size="sm" variant="outline" onClick={() => setFolderDialog({ mode: "create" })} data-testid="new-folder-btn"><FolderPlus className="h-4 w-4" />New Folder</Button>}
        {isAdmin && <Button size="sm" onClick={() => setUploadOpen(true)} data-testid="doc-upload-btn"><Upload className="h-4 w-4" />Upload Document</Button>}
      </PageHeader>

      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-sm mb-4" data-testid="doc-breadcrumb">
        <button onClick={() => setCurrent(null)} className={`hover:text-[#0094A8] ${current ? "text-slate-500" : "font-semibold text-slate-800"}`} data-testid="crumb-library">Document Library</button>
        {current && <><ChevronRight className="h-4 w-4 text-slate-300" /><span className="font-semibold text-slate-800 flex items-center gap-1"><FolderOpen className="h-4 w-4 text-[#0094A8]" />{current.name}</span></>}
      </div>

      <div className="flex flex-col lg:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Search documents…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="doc-search" />
        </div>
        <Select value={catFilter || "all"} onValueChange={(v) => setCatFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full lg:w-52" data-testid="doc-cat-filter"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Categories</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        {isAdmin && (
          <Select value={audFilter || "all"} onValueChange={(v) => setAudFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full lg:w-48" data-testid="doc-aud-filter"><SelectValue placeholder="All Audiences" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Audiences</SelectItem>{AUDIENCES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {isAdmin && <Button variant={showArchived ? "default" : "outline"} size="sm" onClick={() => setShowArchived((v) => !v)} data-testid="doc-archived-toggle">{showArchived ? "Showing Archived" : "Active"}</Button>}
      </div>

      {isAdmin && selected.length > 0 && (
        <div className="flex items-center justify-between rounded-lg bg-[#00C9E0]/8 border border-[#00C9E0]/25 px-4 py-2.5 mb-4" data-testid="doc-bulk-bar">
          <span className="text-sm font-medium text-[#0094A8]">{selected.length} selected</span>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setShareOpen(true)} data-testid="doc-bulk-share"><Share2 className="h-4 w-4" />Share Selected</Button>
            <Button size="sm" variant="outline" onClick={() => setSelected([])}>Clear</Button>
          </div>
        </div>
      )}

      {/* Folders (main library only) */}
      {shownFolders.length > 0 && (
        <div className="mb-6">
          <p className="cc-overline mb-2">Folders</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {shownFolders.map((f) => (
              <div key={f.id} className="cc-card cc-card-hover p-4 flex items-center gap-3 cursor-pointer" onClick={() => setCurrent(f)} data-testid={`folder-${f.id}`}>
                <span className="h-10 w-10 rounded-lg bg-[#9ACC32]/15 text-[#5B880C] flex items-center justify-center shrink-0"><Folder className="h-5 w-5" /></span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display font-semibold text-slate-800 text-sm truncate">{f.name}</h3>
                  <p className="text-xs text-slate-400">{f.document_count} file{f.document_count === 1 ? "" : "s"}</p>
                </div>
                {isAdmin && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><button className="h-8 w-8 flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100" data-testid={`folder-menu-${f.id}`}><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setCurrent(f)}><FolderOpen className="h-4 w-4 mr-2" />Open</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setFolderDialog({ mode: "rename", folder: f })} data-testid={`folder-rename-${f.id}`}><Pencil className="h-4 w-4 mr-2" />Rename</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDelFolder(f)} className="text-[#C42607]" data-testid={`folder-delete-${f.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents */}
      {!current && shownFolders.length > 0 && <p className="cc-overline mb-2">Files</p>}
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-44" />)}</div>
      ) : docs.length === 0 ? (
        <div className="cc-card"><EmptyState icon={current ? FolderOpen : FileText} title={current ? `No documents in ${current.name}` : "No documents"} hint={isAdmin ? "Upload training, media kits, agreements and resources for your team." : "Documents shared with you will appear here."} /></div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {docs.map((d) => {
            const AudIcon = d.audience === "Admin Only" ? Lock : d.audience === "Customer-Facing" ? Globe : Users;
            return (
              <div key={d.id} className="cc-card p-4 flex flex-col" data-testid={`doc-${d.id}`}>
                <div className="flex items-start gap-3">
                  {isAdmin && <Checkbox checked={selected.includes(d.id)} onCheckedChange={() => toggleSel(d.id)} className="mt-1" data-testid={`doc-check-${d.id}`} />}
                  <span className="h-10 w-10 rounded-lg bg-[#00C9E0]/10 text-[#0094A8] flex items-center justify-center shrink-0"><FileText className="h-5 w-5" /></span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display font-semibold text-slate-800 text-sm truncate">{d.title}</h3>
                    <p className="text-xs text-slate-400 truncate">{d.category} · v{d.version} · {(d.size / 1024).toFixed(0)} KB</p>
                  </div>
                </div>
                {d.description && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{d.description}</p>}
                <div className="flex flex-wrap items-center gap-1.5 mt-3">
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600"><AudIcon className="h-3 w-3" />{d.audience}</span>
                  {d.status === "Archived" && <StatusChip value="Archived" tone="slate" />}
                  {(d.tags || []).slice(0, 2).map((t) => <span key={t} className="rounded-full bg-[#9ACC32]/12 text-[#5B880C] px-2 py-0.5 text-[10px]">{t}</span>)}
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => download(d)} data-testid={`doc-download-${d.id}`}><Download className="h-3 w-3" />Open</Button>
                  {isAdmin && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditDoc(d)} data-testid={`doc-edit-${d.id}`}>Edit</Button>}
                  {isAdmin && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setMoveDoc(d)} data-testid={`doc-move-${d.id}`}><FolderInput className="h-3 w-3" /></Button>}
                  {isAdmin && (d.archived ? (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => restore(d)} data-testid={`doc-restore-${d.id}`}><RotateCcw className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs text-[#C42607]" onClick={() => purge(d)} data-testid={`doc-purge-${d.id}`}><Trash2 className="h-3 w-3" /></Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => archive(d)} data-testid={`doc-archive-${d.id}`}><Archive className="h-3 w-3" /></Button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} cats={cats} folders={folders} defaultFolderId={current?.id || ""} onSaved={() => { load(); loadFolders(); }} />
      <EditDialog doc={editDoc} onOpenChange={(o) => !o && setEditDoc(null)} cats={cats} onSaved={load} />
      <MoveDialog doc={moveDoc} folders={folders} onOpenChange={(o) => !o && setMoveDoc(null)} onSaved={() => { load(); loadFolders(); }} />
      <FolderDialog state={folderDialog} onOpenChange={(o) => !o && setFolderDialog(null)} onSave={saveFolder} />
      <DocumentShareDialog open={shareOpen} onOpenChange={setShareOpen} documentIds={selected} onShared={() => { setSelected([]); }} />

      <AlertDialog open={!!delFolder} onOpenChange={(o) => !o && setDelFolder(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this folder?</AlertDialogTitle>
            <AlertDialogDescription>
              {delFolder ? <span className="font-semibold text-slate-700">{delFolder.name}<br /></span> : null}
              The folder will be removed. Any documents inside are kept and moved back to the main Document Library.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteFolder} className="bg-[#C42607] hover:bg-[#9e1e06]" data-testid="folder-confirm-delete">Delete Folder</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FolderDialog({ state, onOpenChange, onSave }) {
  const [name, setName] = useState("");
  useEffect(() => { if (state) setName(state.mode === "rename" ? state.folder?.name || "" : ""); }, [state]);
  return (
    <Dialog open={!!state} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="folder-dialog">
        <DialogHeader><DialogTitle className="font-display">{state?.mode === "rename" ? "Rename Folder" : "New Folder"}</DialogTitle></DialogHeader>
        <div className="py-2">
          <Label className="text-xs">Folder Name</Label>
          <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Zone Maps" data-testid="folder-name-input" autoFocus />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => name.trim() && onSave(name.trim())} data-testid="folder-save">{state?.mode === "rename" ? "Rename" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoveDialog({ doc, folders, onOpenChange, onSaved }) {
  const [dest, setDest] = useState(MAIN);
  useEffect(() => { if (doc) setDest(doc.folder_id || MAIN); }, [doc]);
  const save = async () => {
    try {
      await api.post(`/library/${doc.id}/move`, { folder_id: dest === MAIN ? null : dest });
      toast.success("Document moved"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!doc} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="move-dialog">
        <DialogHeader><DialogTitle className="font-display">Move Document</DialogTitle><DialogDescription>Choose where "{doc?.title}" should live.</DialogDescription></DialogHeader>
        <div className="py-2">
          <Label className="text-xs">Destination</Label>
          <Select value={dest} onValueChange={setDest}>
            <SelectTrigger className="mt-1" data-testid="move-dest"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={MAIN}>Main Library (no folder)</SelectItem>
              {folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} data-testid="move-save">Move</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadDialog({ open, onOpenChange, cats, folders, defaultFolderId, onSaved }) {
  const [form, setForm] = useState({ audience: "All Staff", version: "1.0", status: "Active" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const ref = useRef();
  useEffect(() => { if (open) { setForm({ audience: "All Staff", version: "1.0", status: "Active", category: cats[0]?.name, folder_id: defaultFolderId || MAIN }); setFile(null); } }, [open, cats, defaultFolderId]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    if (!file) { toast.error("Choose a file"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      ["title", "description", "category", "audience", "tags", "version", "status"].forEach((k) => fd.append(k, form[k] || ""));
      fd.append("folder_id", form.folder_id && form.folder_id !== MAIN ? form.folder_id : "");
      await api.post("/library/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="doc-upload-dialog">
        <DialogHeader><DialogTitle className="font-display">Upload Document</DialogTitle><DialogDescription>Stored securely and shared by audience.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-1 max-h-[60vh] overflow-y-auto">
          <div className="sm:col-span-2"><Label className="text-xs">Title *</Label><Input className="mt-1" value={form.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="doc-title" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Textarea className="mt-1" value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="doc-desc" /></div>
          <div><Label className="text-xs">Folder</Label><Select value={form.folder_id || MAIN} onValueChange={(v) => set("folder_id", v)}><SelectTrigger className="mt-1" data-testid="doc-folder"><SelectValue /></SelectTrigger><SelectContent><SelectItem value={MAIN}>Main Library (no folder)</SelectItem>{folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Category</Label><Select value={form.category || ""} onValueChange={(v) => set("category", v)}><SelectTrigger className="mt-1" data-testid="doc-category"><SelectValue placeholder="Select…" /></SelectTrigger><SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Audience</Label><Select value={form.audience} onValueChange={(v) => set("audience", v)}><SelectTrigger className="mt-1" data-testid="doc-audience"><SelectValue /></SelectTrigger><SelectContent>{AUDIENCES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Version</Label><Input className="mt-1" value={form.version} onChange={(e) => set("version", e.target.value)} /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Tags (comma separated)</Label><Input className="mt-1" value={form.tags || ""} onChange={(e) => set("tags", e.target.value)} data-testid="doc-tags" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">File *</Label><Input type="file" className="mt-1" ref={ref} onChange={(e) => setFile(e.target.files?.[0])} data-testid="doc-file" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={saving} data-testid="doc-upload-save">{saving ? "Uploading…" : "Upload"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ doc, onOpenChange, cats, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => { if (doc) setForm({ ...doc, tags: (doc.tags || []).join(", ") }); }, [doc]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      await api.put(`/library/${doc.id}`, { title: form.title, description: form.description, category: form.category, audience: form.audience, version: form.version, status: form.status, tags: form.tags });
      toast.success("Updated"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!doc} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="doc-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">Edit Document</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-1">
          <div className="sm:col-span-2"><Label className="text-xs">Title</Label><Input className="mt-1" value={form.title || ""} onChange={(e) => set("title", e.target.value)} /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Textarea className="mt-1" value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
          <div><Label className="text-xs">Category</Label><Select value={form.category || ""} onValueChange={(v) => set("category", v)}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Audience</Label><Select value={form.audience || ""} onValueChange={(v) => set("audience", v)}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{AUDIENCES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Version</Label><Input className="mt-1" value={form.version || ""} onChange={(e) => set("version", e.target.value)} /></div>
          <div><Label className="text-xs">Tags</Label><Input className="mt-1" value={form.tags || ""} onChange={(e) => set("tags", e.target.value)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={save} data-testid="doc-edit-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
